import ApiService from '@/services/apiService';
import { mapState, mapGetters } from 'vuex';
import routerPaths from './router/paths';
import { themeDefaults, themeColors } from './utils/themes';
import customColorMixin from './mixins/customColor';
import { get, cloneDeep, isEqual, isEmpty } from 'lodash';
export default {
  name: 'App',
  mixins: [customColorMixin],
  data() {
    return {
      eulaKey: 'initial',
      brandingLoaded: false,
      breadcrumbs: null,
      pageTitle: {},
      themesData: {},
      footer: {},
      user: {}
    };
  },
  computed: {
    ...mapState('app', ['load', 'loading', 'language', 'UiTheme', 'color']),
    ...mapGetters({
      sessionUser: 'authentication/user',
      tenantId: 'authentication/tenantId'
    }),
    userId() {
      return get(this, '$store.state.authentication.user.id') || null;
    },
    showEulaDialog() {
      return get(this, '$store.state.authentication.isImpersonating', false) === false && get(this, '$store.state.authentication.status', '') === 'success' && get(this, '$store.state.authentication.user.properties.eula.accepted', false) === false && get(this.$route, 'meta.noAuth', false) === false;
    },
    fontClass() {
      return this.$store.state.app.useAltFonts ? 'font-alt' : '';
    },
    showAppLayout() {
      if (get(this.$route, 'meta.hideLayout')) {
        return false;
      }
      switch (this.$route.path) {
        case '/login':
        case '/error':
        case '/reset-password':
        case '/change-password':
        case '/oauth':
        case '/':
          return false;
        default:
          return true;
      }
    },
    fireflyHost() {
      return process.env.VUE_APP_FIREFLY_HOST;
    },
    host() {
      return `${window.location.protocol}//${window.location.hostname || window.location.host}`;
    }
  },
  watch: {
    $route() {
      this.$nextTick(() => {
        this.updateBreadcrumbs();
      });
    },
    language() {
      this.$nextTick(() => {
        this.updateBreadcrumbs();
      });
    },
    async userId() {
      ApiService.getUser(this).then(async user => {
        this.user = user;
        const defaults = get(user, 'properties.userDefaults');
        if (!defaults || isEmpty(defaults)) {
          await this.loadBranding();
          return;
        }
        this.setLang(user);
        this.setColor(user);
        this.setMapStyle(user);
        this.setUiTheme(user);
        this.$store.state.app.drawer = user.properties.userDefaults.drawer;
        this.$store.state.app.miniVariant = user.properties.userDefaults.miniVariant;
        this.$store.state.app.map3DBuild = user.properties.userDefaults.map3DBuild;
        localStorage.setItem('userSelectedItemsPerList', user.properties.userDefaults.pagination);
      }).catch(e => {
        console.error('error getting user ', e);
      });
      const tenantID = get(this, '$store.state.authentication.tenantId');
      const token = get(this, '$store.state.authentication.token');
      if (tenantID && token) {
        this.getTenantData(tenantID);
      } else {
        console.info('No token found, skipping tenant data loading');
      }
    }
  },
  mounted: async function () {
    await this.loadBranding();
  },
  updated() {
    this.$nextTick(() => {
      this.updateBreadcrumbs();

      // force a store update in case computed showEulaDialog was not updated after login
      if (this.showEulaDialog === false && get(this, '$store.state.authentication.isImpersonating', false) === false && get(this, '$store.state.authentication.status', '') === 'success' && get(this, '$store.state.authentication.user.properties.eula.accepted', false) === false && get(this.$route, 'meta.noAuth', false) === false) {
        this.$store.state.authentication = cloneDeep(this.$store.state.authentication);
      }
    });
  },
  methods: {
    setLang(user) {
      this.$store.commit('app/language', {
        payload: user.properties.userDefaults.language,
        context: this
      });
    },
    setColor(user) {
      const mode = this.$store.state.app.UiTheme ? 'dark' : 'light';
      const colorKey = user.properties.userDefaults.color;
      if (colorKey === 'customerColor') {
        this.$store.state.app.color = mode === 'dark' ? 'customerColorDark' : 'customerColorLight';
        this.$store.state.app.colorHexLight = user.properties.userDefaults.customColor;
        this.$store.state.app.colorHexDark = user.properties.userDefaults.customColor;
        this.$vuetify.theme.themes.light.primary = user.properties.userDefaults.customColor;
        this.$vuetify.theme.themes.dark.primary = user.properties.userDefaults.customColor;
      } else {
        this.$store.state.app.color = user.properties.userDefaults.color;
      }
      localStorage.setItem('thingshiveColorDark', this.$store.state.app.color);
      localStorage.setItem('thingshiveColorLight', this.$store.state.app.color);
    },
    setMapStyle(user) {
      localStorage.setItem('mapStyle', user.properties.userDefaults.mapLayout);
      this.$store.state.app.mapStyle = user.properties.userDefaults.mapLayout;
    },
    setUiTheme(user) {
      const mode = user.properties.userDefaults.uiTheme ? 'dark' : 'light';
      const userHighlight = mode === 'dark' ? localStorage.getItem('thingshiveColorDark') : localStorage.getItem('thingshiveColorLight');
      localStorage.setItem('thingshiveTheme', mode);
      this.$store.state.app.color = userHighlight || this.themes[mode].highlight;
      this.$store.state.app.mode = mode;
      this.$store.state.app.UiTheme = mode === 'dark';
      this.$vuetify.theme.dark = user.properties.userDefaults.uiTheme;
    },
    get,
    updateBreadcrumbs() {
      let newBreadCrumbsPath = null;
      const routerView = get(this.$refs, 'view.$refs.routerview');
      if (routerView && routerView.breadCrumbsPath) {
        newBreadCrumbsPath = routerView.breadCrumbsPath();
      }
      if (!isEqual(this.breadcrumbs, newBreadCrumbsPath)) {
        this.breadcrumbs = newBreadCrumbsPath;
      }
    },
    getLocalUserColor(storageKey, fallback = 'primary') {
      const localColor = localStorage.getItem(storageKey);
      if (themeColors.indexOf(localColor) >= 0) {
        return localColor;
      }
      return fallback;
    },
    getSideBarIconLink(sidebarIconLink) {
      const validLink = routerPaths.filter(path => (path.meta || {}).isDrawerIconLink && path.path === sidebarIconLink);
      return (validLink[0] || {}).path || '/home';
    },
    setFavicon(theme) {
      const link = document.createElement('link');
      link.type = 'image/png';
      link.rel = 'icon';
      if (!theme.favicon || theme.favicon === themeDefaults.favicon) {
        link.href = 'favicon/niotix-x-144x144.png';
      } else {
        link.href = process.env.VUE_APP_API_HOST + theme.favicon;
      }
      document.head.appendChild(link);
    },
    async loadBranding() {
      const tenantId = get(this.$store, 'state.authentication.tenantIdThemeSetting.id') || get(this.$store, 'state.authentication.primaryTenantIds[0]');
      // do not use `this.getThemeMode()` here, because we DON'T want a default-value
      // should use the tenant-branding-theme, IF user did not set up something
      let userMode = String(localStorage.getItem('thingshiveTheme')).toLocaleLowerCase();
      if (userMode !== 'dark' && userMode !== 'light') {
        userMode = this.$store.state.app.mode || null;
      }
      const theme = await this.$store.dispatch('app/loadBranding', {
        hostUri: this.host,
        tenantId,
        userThemeMode: userMode,
        context: this
      });
      this.pageTitle = this.$store.state.app.pageTitle;
      this.footer = this.$store.state.app.footer;
      this.$store.state.app.sidebarIconLink = this.getSideBarIconLink(theme.sidebarIconLink);
      this.$store.state.app.grafanaUrl = theme.grafanaUrl;
      this.$store.state.app.fireflyUrl = `https://${this.fireflyHost}`;

      // use vollkorn as default font as long as the digimondo logo was not changed
      const useAltFonts = theme.useAltFonts === undefined ? !this.isDefaultSvgLogo() : theme.useAltFonts;
      this.$store.commit('app/useAltFonts', {
        payload: useAltFonts
      });
      this.$store.state.app.useCustomBackground = theme.useCustomBackground === undefined ? themeDefaults.useCustomBackground : theme.useCustomBackground;
      this.setFavicon(theme);

      // set vue ui mode
      this.$vuetify.theme.dark = this.themeGetMode() === 'dark';
      this.themesData = this.$store.state.app.themes;
      this.brandingLoaded = true;
    },
    getTenantData(tenantId) {
      ApiService.tenantGetById(tenantId, ['no-descendents']).then(tenants => {
        const feedbackEmail = get(tenants, 'data.feedback.bug') || 'support@digimondo.de';
        this.$store.commit('app/setFeedbackEmail', feedbackEmail);
      }).catch(error => {
        // only print error-messages for tenant-data, if this is not a redirect
        // in case of Node-RED or Grafana opening, it will shortly display the login-page before redirecting;
        // anything other than the Auth shall not disturb the user, so an error fetching the tenant-data is not pertinent
        if (!get(this.$router, 'query.redirect')) {
          if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
            this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
          } else {
            this.$snack(this.$i18n_t('Common.tenantReadFail'), 'error');
          }
        }
        this.$store.commit('app/setFeedbackEmail', 'support@digimondo.de');
      }).finally(() => {
        this.$store.state.app.loading = false;
      });
    }
  }
};